import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getSystemNotifications } from "../api/system-notifications";
import {
  clearSystemNotifications,
  setSystemNotifications,
} from "../redux/system-notifications/system-notifications-slice";
import { RootState } from "../store";
import { SUCCESS } from "../utils/constants/tags";

const REFRESH_INTERVAL_MS = 2 * 60 * 1000;

interface SystemNotificationsProviderProps {
  children: React.ReactNode;
}

/**
 * Loads the active notifications once for the whole app and refreshes them
 * periodically, so the banners rendered on the document screens do not each
 * own a request.
 */
const SystemNotificationsProvider: React.FunctionComponent<
  SystemNotificationsProviderProps
> = (props) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );

  const dispatch = useDispatch();
  const accessBlocked = taxPayer.accessBlocked === true;

  useEffect(() => {
    // A suspended company refuses the request; there is nothing to poll.
    if (taxPayer.id === undefined || accessBlocked) {
      dispatch(clearSystemNotifications());
      return;
    }

    let cancelled = false;

    const loadNotifications = async () => {
      const status = await getSystemNotifications();

      if (cancelled) {
        return;
      }

      if (status.status === SUCCESS && status.data !== undefined) {
        dispatch(setSystemNotifications(status.data.items));
      }
    };

    loadNotifications();

    const interval = setInterval(loadNotifications, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer.id, accessBlocked]);

  return <>{props.children}</>;
};

export default SystemNotificationsProvider;
